import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { formatDate } from "@redotech/redo-web/time";
import { Tooltip as DarkTooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo } from "react";

export interface OrderVolumeMetricsSnapshot {
  onlineStoreOrderVolume?: OrderVolumeSnapshot | null;
  totalOrderVolume?: OrderVolumeSnapshot | null;
}

interface OrderVolumeSnapshot {
  recordedAt?: Date | string | Temporal.Instant | null;
  last30Days?: number | null;
  lastYear?: number | null;
}

const formatOrderCount = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString();

const formatOptionalDate = (
  value: string | Date | Temporal.Instant | null | undefined,
) => {
  if (!value) {
    return "—";
  }

  try {
    const instantInput =
      value instanceof Date ? value.toISOString() : value.toString();
    return formatDate(Temporal.Instant.from(instantInput));
  } catch {
    return "—";
  }
};

export const OrderVolumeTooltipCell = memo(function OrderVolumeTooltipCell({
  snapshot,
}: {
  snapshot: OrderVolumeMetricsSnapshot | null | undefined;
}) {
  const online = snapshot?.onlineStoreOrderVolume;
  const total = snapshot?.totalOrderVolume;
  const displayValue = formatOrderCount(online?.lastYear);
  const recordedAt = online?.recordedAt ?? total?.recordedAt;

  if (!online && !total) {
    return (
      <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
        —
      </Text>
    );
  }

  const metricRows = (
    <Flex dir="column" gap="xs">
      <Flex align="baseline" gap="xl" justify="flex-end">
        <Text
          fontSize="xs"
          fontWeight="semibold"
          textColor="tooltip-supporting-text"
        >
          Online store
        </Text>
        <Text
          fontSize="xs"
          fontWeight="semibold"
          textColor="tooltip-supporting-text"
        >
          Total
        </Text>
      </Flex>
      <Flex align="baseline" gap="xl" justify="space-between">
        <Text
          fontSize="xs"
          fontWeight="medium"
          textColor="tooltip-supporting-text"
        >
          Last 30 days
        </Text>
        <Flex align="baseline" gap="xl">
          <Text
            fontFamily="monospace"
            fontSize="xs"
            fontWeight="medium"
            textColor="tooltip-calculation-text"
          >
            {formatOrderCount(online?.last30Days)}
          </Text>
          <Text
            fontFamily="monospace"
            fontSize="xs"
            fontWeight="medium"
            textColor="tooltip-calculation-text"
          >
            {formatOrderCount(total?.last30Days)}
          </Text>
        </Flex>
      </Flex>
      <Flex align="baseline" gap="xl" justify="space-between">
        <Text
          fontSize="xs"
          fontWeight="medium"
          textColor="tooltip-supporting-text"
        >
          Last year
        </Text>
        <Flex align="baseline" gap="xl">
          <Text
            fontFamily="monospace"
            fontSize="xs"
            fontWeight="medium"
            textColor="tooltip-calculation-text"
          >
            {formatOrderCount(online?.lastYear)}
          </Text>
          <Text
            fontFamily="monospace"
            fontSize="xs"
            fontWeight="medium"
            textColor="tooltip-calculation-text"
          >
            {formatOrderCount(total?.lastYear)}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );

  const tooltipContent = (
    <Flex dir="column" gap="sm">
      <Flex dir="column" gap="xxs">
        <Text fontSize="xs" fontWeight="semibold" textColor="white">
          Order metrics
        </Text>
        {recordedAt && (
          <Text
            fontSize="xs"
            fontWeight="medium"
            textColor="tooltip-supporting-text"
          >
            Snapshot: {formatOptionalDate(recordedAt)}
          </Text>
        )}
      </Flex>
      {metricRows}
    </Flex>
  );

  return (
    <DarkTooltip
      arrow
      leftAlign
      paddingOverride="12px 12px"
      title={tooltipContent}
    >
      <Flex as="span" gap="none">
        <Text fontSize="xs" fontWeight="regular" textColor="primary">
          {displayValue}
        </Text>
      </Flex>
    </DarkTooltip>
  );
});
